import { useEffect, useMemo, useRef, useState } from "react";
import { C } from "../../shared/theme";
import { categoryLabel } from "../../shared/categories";
import { goalColorTokens } from "../../shared/goalColors";
import { EditorFrame, fieldLabel, DeleteButton } from "../../shared/EditorFrame";
import { STATEMENT_KINDS, statementText } from "./statementKinds";
import { suggestGoals } from "./suggestGoals";
import { growToFit } from "../../shared/growToFit";


// Adding or editing one vision statement or fear: its words, its category, and the goals it links to.
// `statement` is null for a new one. onSave(fields) and onDelete() return promises.
export function StatementEditor({ kind, statement, goals, categories, onSave, onDelete, onClose }) {
  const k = STATEMENT_KINDS[kind];
  const isNew = !statement;
  const [text, setText] = useState(statement ? statementText(kind, statement) : "");
  const [categoryId, setCategoryId] = useState(statement?.categoryId || null);
  // Links to goals that have since been deleted are dropped here, so saving cleans them up.
  const [initiallyLinked] = useState(() => new Set((statement?.goalIds || []).filter(id => goals.some(g => g.id === id))));
  const [linked, setLinked] = useState(initiallyLinked);
  const [suggested, setSuggested] = useState(new Set());
  // Goals the user ticked or unticked themselves (suggestions never change those), and the goals the
  // last suggestion ticked (so a new wording can untick them).
  const touched = useRef(new Set());
  const autoTicked = useRef(new Set());
  const [suggesting, setSuggesting] = useState(false);
  const [showInspire, setShowInspire] = useState(false);
  const [busy, setBusy] = useState(null); // "save" | "delete"
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const textRef = useRef(null);
  const canSave = text.trim() && !busy;

  // Active goals, plus any finished or parked goal this was linked to when opened, so it can be unlinked.
  const choices = useMemo(() => goals.filter(g => g.status === "active" || initiallyLinked.has(g.id)), [goals, initiallyLinked]);
  const activeGoals = useMemo(() => goals.filter(g => g.status === "active"), [goals]);
  // Read by the suggestions, which re-run when the words change, not every time a goal changes.
  const activeGoalsRef = useRef(activeGoals);
  activeGoalsRef.current = activeGoals;

  useEffect(() => { growToFit(textRef.current); }, []);

  // While writing a new one, tick the goals it most likely connects to. The user can untick them.
  useEffect(() => {
    if (!isNew || text.trim().length < 10 || activeGoalsRef.current.length === 0) { setSuggested(new Set()); return undefined; }
    let live = true;
    const timer = setTimeout(async () => {
      setSuggesting(true);
      try {
        const ids = new Set(await suggestGoals(kind, text.trim(), activeGoalsRef.current));
        if (!live) return;
        const fresh = [...ids].filter(id => !touched.current.has(id));
        const stale = [...autoTicked.current].filter(id => !ids.has(id) && !touched.current.has(id));
        autoTicked.current = new Set(fresh);
        setLinked(prev => new Set([...[...prev].filter(id => !stale.includes(id)), ...fresh]));
        setSuggested(ids);
      } catch (e) { console.error("Goal suggestions failed:", e); }
      finally { if (live) setSuggesting(false); }
    }, 700);
    return () => { live = false; clearTimeout(timer); };
  }, [text, isNew, kind]);

  const toggle = (id) => {
    touched.current.add(id);
    setLinked(prev => {
    const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    if (!canSave) return;
    setBusy("save");
    setError(null);
    try {
      await onSave({
        text: text.trim(), categoryId,
        categoryLabel: categories.find(c => c.id === categoryId)?.label || "",
        goalIds: [...linked],
      });
      onClose();
    } catch (err) {
      console.error(`Saving a ${k.one} failed:`, err);
      setError(`We couldn't save your ${k.one}. Check your connection and try again.`);
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setBusy("delete");
    try { await onDelete(); onClose(); }
    catch (err) {
      console.error(`Deleting a ${k.one} failed:`, err);
      setError(`We couldn't delete your ${k.one}. Check your connection and try again.`);
      setBusy(null);
    }
  };

  return (
    <EditorFrame id="statement-editor-title" title={isNew ? k.newTitle : k.editTitle}
      canSave={canSave} saving={busy === "save"} busy={!!busy} onSave={save} onClose={onClose}>
      <div>
        <label htmlFor="statement-text" style={{ ...fieldLabel(), display: "block" }}>{k.textLabel}</label>
        <textarea id="statement-text" ref={textRef} value={text} rows={3} autoFocus={isNew} placeholder={k.placeholder}
          onChange={e => { setText(e.target.value); growToFit(e.target); }}
          style={{ width: "100%", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "12px 14px", fontSize: 16, color: C.text, fontFamily: "inherit", resize: "none", boxSizing: "border-box", lineHeight: 1.5, overflow: "hidden" }} />
      </div>

      {/* Examples only while the box is empty, so picking one never replaces the user's words. */}
      {isNew && !text.trim() && (
        <div>
          <button onClick={() => setShowInspire(v => !v)} aria-expanded={showInspire} aria-controls="statement-examples"
            style={{ width: "100%", minHeight: 48, display: "flex", alignItems: "center", gap: 10, background: C.accentLight, border: `1px solid ${C.accentMid}`, borderRadius: C.rsm, padding: "10px 14px", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
            <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>💡</span>
            <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: C.textGreen }}>{showInspire ? "Hide the examples" : "Need ideas? See some examples"}</span>
            <span aria-hidden="true" style={{ fontSize: 12, color: C.textGreen }}>{showInspire ? "▲" : "▼"}</span>
          </button>
          {showInspire && (
            <div id="statement-examples" style={{ marginTop: 8, background: C.surface, border: `1px solid ${C.borderCard}`, borderRadius: C.r, overflow: "hidden" }}>
              {k.inspiration.map((group, gi) => (
                <div key={group.cat} style={{ borderTop: gi ? `1px solid ${C.border}` : "none" }}>
                  <div style={{ padding: "10px 14px 4px", fontSize: 12, fontWeight: 700, color: C.textMid, letterSpacing: "0.06em", textTransform: "uppercase" }}>{group.cat}</div>
                  {group.items.map(example => (
                    <button key={example} onClick={() => { setText(example); setShowInspire(false); requestAnimationFrame(() => { growToFit(textRef.current); textRef.current?.focus(); }); }}
                      style={{ display: "block", width: "100%", textAlign: "left", minHeight: 44, padding: "10px 14px", fontSize: 15, lineHeight: 1.4, color: C.text, background: "none", border: "none", borderTop: `1px solid ${C.border}`, cursor: "pointer", fontFamily: "inherit" }}>
                      {example}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <label htmlFor="statement-category" style={{ ...fieldLabel(), display: "block" }}>Category</label>
        <select id="statement-category" value={categoryId || ""} onChange={e => setCategoryId(e.target.value || null)}
          style={{ width: "100%", minHeight: 44, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: C.rsm, padding: "0 14px", fontSize: 16, color: categoryId ? C.text : C.textMid, fontFamily: "inherit" }}>
          <option value="">General</option>
          {categories.map(cat => <option key={cat.id} value={cat.id}>{categoryLabel(cat)}</option>)}
        </select>
      </div>

      <div>
        <div style={{ ...fieldLabel(), display: "flex", alignItems: "center", gap: 8 }}>
          <span id="statement-goals-label">{k.goalsLabel}</span>
          {suggesting && <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>Finding matches…</span>}
        </div>
        {choices.length === 0 ? (
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: C.textMid }}>You don't have any active goals to link yet.</p>
        ) : (
          <div role="group" aria-labelledby="statement-goals-label" style={{ background: C.surface, border: `1px solid ${C.borderCard}`, borderRadius: C.r, overflow: "hidden" }}>
            {choices.map((goal, i) => {
              const checked = linked.has(goal.id);
              const tok = goalColorTokens(goal.color);
              return (
                <button key={goal.id} role="checkbox" aria-checked={checked} onClick={() => toggle(goal.id)}
                  style={{ width: "100%", minHeight: 48, padding: "10px 14px", borderTop: i ? `1px solid ${C.border}` : "none", borderLeft: "none", borderRight: "none", borderBottom: "none", background: "none", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                  <span aria-hidden="true" style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${checked ? C.accent : C.textDim}`, background: checked ? C.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {checked && <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke={C.onAccent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="2,6 5,9 10,3" /></svg>}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 15, lineHeight: 1.4, color: C.text, fontWeight: checked ? 600 : 400 }}>{goal.title}</span>
                  {suggested.has(goal.id) && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.text, background: tok.bg, borderRadius: 100, padding: "2px 8px", flexShrink: 0 }}>Suggested</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {error && <p role="alert" style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: C.red }}>{error}</p>}

      {!isNew && <DeleteButton noun={k.one} confirming={confirmDelete} deleting={busy === "delete"} disabled={!!busy} onClick={remove} />}
    </EditorFrame>
  );
}
